import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, Optional, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Workbook } from 'exceljs';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';

 import { CitationsApiService, CitationSourceMetadataParams } from './citations-api.service';
import { formatMDY } from '~/core/utils/date-format';

interface CitationsManifestDetail {
    citation_id?: string;
    source_file?: string;
    source_type?: string;
    text_snippet?: string;
    date?: string;
    event_id?: string;
    page_number?: number | null;
    row_number?: number | null;
    row_index?: number | null;
    row_uid?: string | null;
    row_uid_column?: string | null;
    [key: string]: unknown;
}

interface CitationsManifestResponse {
    by_asset?: Record<string, string[]>;
    citations?: Record<string, CitationsManifestDetail>;
}

interface CitationTimelineItem {
    citation_id: string;
    source_file: string;
    source_type: string;
    text_snippet: string;
    date: string;
    page_number?: number | null;
    bbox?: [number, number, number, number] | null;
    row_number?: number | null;
    row_index?: number | null;
    row_uid?: string | null;
    row_uid_column?: string | null;
    event_id?: string;
}

type SourcePreviewType = 'pdf' | 'csv' | 'excel' | 'delta' | 'unsupported' | 'none';

interface CitationDeltaSourceResponse {
    source_file_name: string;
    container: string;
    type: string;
    row_uid: string;
    row_uid_column: string;
    row_count: number;
    columns: string[];
    rows: Array<Record<string, unknown>>;
}

interface DeltaFieldRow {
    key: string;
    value: string;
    parts: SourceTextPart[];
}

interface SourceTextPart {
    text: string;
    highlighted: boolean;
}

interface CitationTimelineResponse {
    items: CitationTimelineItem[];
    asset_tag: string;
}

interface CitationAssetDetailsResponse {
    asset_tag: string;
    main_concern?: string;
    narrative?: string;
    recommended_action?: string;
    risk_type?: string;
    failure_type?: string;
    severity?: number;
    trend_worsening?: number;
    risk_score?: number;
    risk_band?: string;
    criticality?: string;
    cm_wos?: number;
    cm_cost?: number;
    is_bad_actor?: boolean;
    citation_count?: number;
    grounding?: string;
    grounding_note?: string;
    trend_summary?: string;
}

interface TabularPreviewRow {
    cells: string[];
    dataRowNumber: number;
    isTarget: boolean;
}

interface PdfPreviewPage {
    pageNumber: number;
    width: number;
    height: number;
    scale: number;
    highlight: { left: number; top: number; width: number; height: number } | null;
}

@Component({
    selector: 'app-citations',
    templateUrl: './citations.component.html',
    styleUrls: ['./citations.component.scss'],
    standalone: false
})
export class CitationsComponent implements OnInit, OnDestroy {
    @ViewChild('citationsGrid') private citationsGridRef?: ElementRef<HTMLDivElement>;
    @ViewChild('csvTableContainer') private csvTableContainerRef?: ElementRef<HTMLDivElement>;
    @ViewChild('excelTableContainer') private excelTableContainerRef?: ElementRef<HTMLDivElement>;
    @ViewChild('pdfViewerContainer') private pdfViewerContainerRef?: ElementRef<HTMLDivElement>;
    @ViewChildren('pdfPageCanvas') private pdfPageCanvasRefs?: QueryList<ElementRef<HTMLCanvasElement>>;
    @ViewChild('deltaRecordsContainer') private deltaRecordsContainerRef?: ElementRef<HTMLDivElement>;

    public leftPanelWidthPercent = 24;
    public centerPanelWidthPercent = 42;
    public rightPanelWidthPercent = 34;

    private readonly minLeftPanelPercent = 16;
    private readonly minCenterPanelPercent = 24;
    private readonly minRightPanelPercent = 20;

    private activeResizeSide: 'left' | 'right' | null = null;
    private windowMouseMoveHandler?: (event: MouseEvent) => void;
    private windowMouseUpHandler?: () => void;
    public refineryId = '';
    public unitId = '';
    public runId = '';
    public selectedAssetTag = '';

    public assetTagOptions: string[] = [];
    public sourceFiles: string[] = [];

    public selectedSourceFile = '';
    public filteredTimelineItems: CitationTimelineItem[] = [];
    public activeCitationIndex = -1;

    public selectedCitationDetail: CitationsManifestDetail | null = null;
    public selectedAssetEvidence: CitationAssetDetailsResponse | null = null;

    public isLoadingManifest = false;
    public isLoadingTimeline = false;
    public isLoadingEvidenceDetails = false;

    public hasManifestLoadError = false;
    public hasTimelineLoadError = false;
    public hasEvidenceDetailsLoadError = false;

    public selectedSourceFileUrl = '';
    public selectedSourceFileName = '';
    public sourcePreviewType: SourcePreviewType = 'none';
    public sourcePreviewError = '';
    public isLoadingSourcePreview = false;
    public sourceCsvRows: string[][] = [];
    public sourceExcelRows: string[][] = [];
    public sourceCsvVisibleRows: TabularPreviewRow[] = [];
    public sourceExcelVisibleRows: TabularPreviewRow[] = [];
    public sourceCsvHighlightedRowNumber: number | null = null;
    public sourceExcelHighlightedRowNumber: number | null = null;
    public sourceDeltaRecords: DeltaFieldRow[][] = [];
    public sourceDeltaRowCount = 0;
    public pdfPageNumber = 0;
    public pdfPages: PdfPreviewPage[] = [];

    private citationsManifest: CitationsManifestResponse | null = null;
    private timelineItems: CitationTimelineItem[] = [];
    private activeSourceObjectUrl = '';
    private sourcePreviewRequestId = 0;
    private renderedPdfKey = '';

    constructor(
        @Optional() private readonly route?: ActivatedRoute,
        @Optional() private readonly citationsApiService?: CitationsApiService,
        @Optional() private readonly changeDetectorRef?: ChangeDetectorRef
    ) {}

    public ngOnInit(): void {
        if (!this.route) {
            return;
        }

        this.route.paramMap.subscribe((params) => {
            this.refineryId = params.get('refineryId')?.trim() ?? '';
            this.unitId = params.get('unitId')?.trim() ?? '';
            this.runId = params.get('runId')?.trim() ?? '';
            const assetTag = params.get('assetTag')?.trim() ?? '';

            if (!this.refineryId || !this.unitId || !this.runId) {
                this.resetAllState();
                this.hasManifestLoadError = true;
                return;
            }

            this.loadManifest(assetTag);
        });
    }

    public ngOnDestroy(): void {
        this.detachResizeListeners();
        this.revokeSourceObjectUrl();
    }

    public onAssetSelectionChange(assetTag: string): void {
        const nextAssetTag = assetTag?.trim() ?? '';
        if (!nextAssetTag || nextAssetTag === this.selectedAssetTag) {
            return;
        }

        this.selectedAssetTag = nextAssetTag;
        this.loadTimelineForAsset(nextAssetTag);
        this.loadEvidenceDetailsForAsset(nextAssetTag);
    }

    public onSourceFileClick(fileName: string): void {
        this.selectedSourceFile = fileName;
        this.rebuildFilteredTimeline();

        if (this.filteredTimelineItems.length > 0) {
            this.selectCitationByIndex(0);
        } else {
            this.activeCitationIndex = -1;
            this.selectedCitationDetail = null;
        }
    }

    public onTimelineDotClick(index: number): void {
        if (index < 0 || index >= this.filteredTimelineItems.length) {
            return;
        }

        this.selectCitationByIndex(index);
    }

    public getTimelineDateLabel(dateValue: string | undefined): string {
        if (!dateValue) {
            return '--';
        }

        const formatted = formatMDY(dateValue);
        return formatted || dateValue;
    }

    public getTrendSummaryWithDatesOnly(trendSummary: string | undefined): string[] {
        const normalized = (trendSummary ?? '').trim();

        if (!normalized) {
            return [];
        }

        return normalized
            .split(/\r?\n+/)
            .map((line) => line.replace(/^\s*[•\-*]\s*/, '').trim())
            .filter((line) => this.isDatePrefixedTrendLine(line));
    }

    public get activeCitation(): CitationTimelineItem | null {
        if (this.activeCitationIndex < 0 || this.activeCitationIndex >= this.filteredTimelineItems.length) {
            return null;
        }

        return this.filteredTimelineItems[this.activeCitationIndex];
    }

    public get timelineCountLabel(): string {
        if (this.filteredTimelineItems.length === 0) {
            return 'No citations';
        }

        return `${this.activeCitationIndex + 1} / ${this.filteredTimelineItems.length}`;
    }

    private isDatePrefixedTrendLine(line: string): boolean {
        return /^((?:19|20)\d{2}[-/](?:0[1-9]|1[0-2])(?:[-/](?:0[1-9]|[12]\d|3[01]))?)\s*[:\-]\s*.+$/.test(line);
    }

    public onResizeStart(side: 'left' | 'right', event: MouseEvent): void {
        if (window.innerWidth <= 1080) {
            return;
        }

        event.preventDefault();
        this.activeResizeSide = side;

        this.windowMouseMoveHandler = (moveEvent: MouseEvent) => this.onResizeMove(moveEvent);
        this.windowMouseUpHandler = () => this.onResizeEnd();

        window.addEventListener('mousemove', this.windowMouseMoveHandler);
        window.addEventListener('mouseup', this.windowMouseUpHandler);
    }

    private loadManifest(preferredAssetTag: string): void {
        if (!this.citationsApiService || this.isLoadingManifest) {
            return;
        }

        this.isLoadingManifest = true;
        this.hasManifestLoadError = false;

        this.citationsApiService.getManifest<CitationsManifestResponse>(this.runId, this.refineryId, this.unitId).subscribe({
            next: (response) => {
                this.citationsManifest = response ?? {};
                this.assetTagOptions = Object.keys(this.citationsManifest.by_asset ?? {}).sort((a, b) => a.localeCompare(b));

                const initialAsset = this.resolveInitialAssetTag(preferredAssetTag);
                this.selectedAssetTag = initialAsset;

                this.isLoadingManifest = false;

                if (!initialAsset) {
                    this.resetAssetScopedState();
                    return;
                }

                this.loadTimelineForAsset(initialAsset);
                this.loadEvidenceDetailsForAsset(initialAsset);
            },
            error: () => {
                this.isLoadingManifest = false;
                this.hasManifestLoadError = true;
                this.resetAssetScopedState();
            }
        });
    }

    private loadTimelineForAsset(assetTag: string): void {
        if (!this.citationsApiService || !assetTag) {
            return;
        }

        this.isLoadingTimeline = true;
        this.hasTimelineLoadError = false;

        this.citationsApiService.getAssetTimeline<CitationTimelineResponse>(
            this.runId,
            this.refineryId,
            this.unitId,
            assetTag
        ).subscribe({
            next: (response) => {
                this.timelineItems = response?.items ?? [];
                this.sourceFiles = this.buildDistinctSourceFiles(this.timelineItems);

                this.isLoadingTimeline = false;

                if (this.selectedSourceFile && !this.sourceFiles.includes(this.selectedSourceFile)) {
                    this.selectedSourceFile = '';
                }

                this.rebuildFilteredTimeline();

                if (this.filteredTimelineItems.length > 0) {
                    this.selectCitationByIndex(0);
                } else {
                    this.activeCitationIndex = -1;
                    this.selectedCitationDetail = null;
                }
            },
            error: () => {
                this.isLoadingTimeline = false;
                this.hasTimelineLoadError = true;
                this.timelineItems = [];
                this.sourceFiles = [];
                this.filteredTimelineItems = [];
                this.activeCitationIndex = -1;
                this.selectedCitationDetail = null;
            }
        });
    }

    private loadEvidenceDetailsForAsset(assetTag: string): void {
        if (!this.citationsApiService || !assetTag) {
            return;
        }

        this.isLoadingEvidenceDetails = true;
        this.hasEvidenceDetailsLoadError = false;

        this.citationsApiService.getAssetDetails<CitationAssetDetailsResponse>(
            this.runId,
            this.refineryId,
            this.unitId,
            assetTag
        ).subscribe({
            next: (response) => {
                this.selectedAssetEvidence = response ?? null;
                this.isLoadingEvidenceDetails = false;
            },
            error: () => {
                this.selectedAssetEvidence = null;
                this.hasEvidenceDetailsLoadError = true;
                this.isLoadingEvidenceDetails = false;
            }
        });
    }

    private selectCitationByIndex(index: number): void {
        if (index < 0 || index >= this.filteredTimelineItems.length) {
            return;
        }

        this.activeCitationIndex = index;

        const selectedItem = this.filteredTimelineItems[index];
        const citationId = selectedItem.citation_id;
        this.selectedCitationDetail = this.citationsManifest?.citations?.[citationId] ?? null;

        const selectedSourceFile = (selectedItem.source_file ?? '').trim();
        if (selectedSourceFile) {
            this.selectedSourceFile = selectedSourceFile;
            this.updateSourcePreview(selectedSourceFile, selectedItem);
        } else {
            this.resetSourcePreview();
        }
    }

    private rebuildFilteredTimeline(): void {
        if (!this.selectedSourceFile) {
            this.filteredTimelineItems = [...this.timelineItems];
            return;
        }

        this.filteredTimelineItems = this.timelineItems.filter(
            (item) => (item.source_file || '').toLowerCase() === this.selectedSourceFile.toLowerCase()
        );
    }

    private buildDistinctSourceFiles(items: CitationTimelineItem[]): string[] {
        const uniqueFiles = new Set<string>();

        items.forEach((item) => {
            const sourceFile = (item.source_file ?? '').trim();
            if (sourceFile) {
                uniqueFiles.add(sourceFile);
            }
        });

        return Array.from(uniqueFiles).sort((a, b) => a.localeCompare(b));
    }

    private resolveInitialAssetTag(preferredAssetTag: string): string {
        if (preferredAssetTag && this.assetTagOptions.includes(preferredAssetTag)) {
            return preferredAssetTag;
        }

        return this.assetTagOptions[0] ?? '';
    }

    private resetAllState(): void {
        this.citationsManifest = null;
        this.assetTagOptions = [];
        this.selectedAssetTag = '';
        this.resetAssetScopedState();
        this.hasManifestLoadError = false;
        this.hasTimelineLoadError = false;
        this.hasEvidenceDetailsLoadError = false;
        this.isLoadingManifest = false;
        this.isLoadingTimeline = false;
        this.isLoadingEvidenceDetails = false;
    }

    private resetAssetScopedState(): void {
        this.timelineItems = [];
        this.filteredTimelineItems = [];
        this.sourceFiles = [];
        this.selectedSourceFile = '';
        this.activeCitationIndex = -1;
        this.selectedCitationDetail = null;
        this.selectedAssetEvidence = null;
        this.resetSourcePreview();
    }

    private updateSourcePreview(sourceFile: string, timelineItem?: CitationTimelineItem): void {
        if (!this.citationsApiService) {
            this.resetSourcePreview();
            return;
        }

        const trimmed = sourceFile.trim();
        if (!trimmed) {
            this.resetSourcePreview();
            return;
        }

        const extension = trimmed.split('.').pop()?.toLowerCase() ?? '';
        const previewType = this.resolveSourcePreviewType(extension, timelineItem);

        // Re-selecting a citation inside the same already-rendered PDF only needs a highlight update.
        if (previewType === 'pdf') {
            const pdfKey = this.buildPdfPreviewKey(trimmed);
            if (this.renderedPdfKey === pdfKey && this.pdfPages.length > 0) {
                this.sourcePreviewRequestId += 1;
                this.selectedSourceFileName = trimmed;
                this.sourcePreviewType = 'pdf';
                this.sourcePreviewError = '';
                this.isLoadingSourcePreview = false;
                this.updatePdfHighlight(timelineItem);
                this.changeDetectorRef?.detectChanges();
                setTimeout(() => this.scrollPdfHighlightIntoView(), 0);
                return;
            }
        }

        this.revokeSourceObjectUrl();
        this.selectedSourceFileName = trimmed;
        this.selectedSourceFileUrl = '';
        this.sourcePreviewError = '';
        this.isLoadingSourcePreview = true;
        this.sourceCsvRows = [];
        this.sourceExcelRows = [];
        this.sourceCsvVisibleRows = [];
        this.sourceExcelVisibleRows = [];
        this.sourceCsvHighlightedRowNumber = null;
        this.sourceExcelHighlightedRowNumber = null;
        this.sourceDeltaRecords = [];
        this.sourceDeltaRowCount = 0;
        this.pdfPages = [];
        this.pdfPageNumber = 0;
        this.renderedPdfKey = '';

        const requestId = this.sourcePreviewRequestId + 1;
        this.sourcePreviewRequestId = requestId;

        this.sourcePreviewType = previewType;

        // The source endpoint only serves delta/pdf/excel/csv, so never request anything else.
        if (this.sourcePreviewType === 'unsupported') {
            this.sourcePreviewError = 'Inline preview is not available for this source type.';
            this.isLoadingSourcePreview = false;
            return;
        }

        const metadataParams: CitationSourceMetadataParams = {
            sourceFileName: trimmed,
            type: this.sourcePreviewType,
            rowUid: timelineItem?.row_uid ?? null,
            rowUidColumn: timelineItem?.row_uid_column ?? null
        };

        if (this.sourcePreviewType === 'delta') {
            this.loadDeltaSourcePreview(metadataParams, requestId, timelineItem);
            return;
        }

        this.citationsApiService.getSourceByMetadataBlob(
            metadataParams,
            this.runId,
            this.refineryId,
            this.unitId
        ).subscribe({
            next: (sourceBlob) => {
                if (requestId !== this.sourcePreviewRequestId) {
                    return;
                }

                if (this.sourcePreviewType === 'pdf') {
                    this.renderPdfCitation(sourceBlob, timelineItem, requestId);
                    return;
                }

                this.revokeSourceObjectUrl();
                this.activeSourceObjectUrl = URL.createObjectURL(sourceBlob);
                this.selectedSourceFileUrl = this.activeSourceObjectUrl;

                if (this.sourcePreviewType === 'csv') {
                    sourceBlob
                        .text()
                        .then((text) => {
                            this.sourceCsvRows = this.parseCsvRows(text);
                            const csvPreview = this.buildTabularPreviewRows(this.sourceCsvRows, timelineItem);
                            this.sourceCsvVisibleRows = csvPreview.rows;
                            this.sourceCsvHighlightedRowNumber = csvPreview.targetRowNumber;
                            this.isLoadingSourcePreview = false;
                            // Use setTimeout to ensure DOM is fully rendered before scrolling
                            setTimeout(() => {
                                this.scrollTabularTargetRowIntoView('csv');
                            }, 100);
                        })
                        .catch(() => {
                            this.sourcePreviewError = 'Unable to parse CSV source preview.';
                            this.sourcePreviewType = 'unsupported';
                            this.isLoadingSourcePreview = false;
                        });
                    return;
                }

                if (this.sourcePreviewType === 'excel') {
                    sourceBlob
                        .arrayBuffer()
                        .then(async (buffer) => {
                            const workbook = new Workbook();
                            await workbook.xlsx.load(buffer as any);
                            const worksheet = workbook.worksheets[0];

                            if (!worksheet) {
                                this.sourceExcelRows = [];
                                this.isLoadingSourcePreview = false;
                                return;
                            }

                            const sheetRows: string[][] = [];
                            worksheet.eachRow({ includeEmpty: true }, (row) => {
                                const cells: string[] = [];
                                const maxCellCount = Math.max(worksheet.columnCount, row.cellCount);
                                for (let cellIndex = 1; cellIndex <= maxCellCount; cellIndex += 1) {
                                    cells.push(row.getCell(cellIndex).text ?? '');
                                }
                                sheetRows.push(cells);
                            });
                            this.sourceExcelRows = sheetRows;
                            const excelPreview = this.buildTabularPreviewRows(this.sourceExcelRows, timelineItem);
                            this.sourceExcelVisibleRows = excelPreview.rows;
                            this.sourceExcelHighlightedRowNumber = excelPreview.targetRowNumber;
                            this.isLoadingSourcePreview = false;
                            // Use setTimeout to ensure DOM is fully rendered before scrolling
                            setTimeout(() => {
                                this.scrollTabularTargetRowIntoView('excel');
                            }, 100);
                        })
                        .catch(() => {
                            this.sourcePreviewError = 'Unable to parse Excel source preview.';
                            this.isLoadingSourcePreview = false;
                        });
                    return;
                }

                this.isLoadingSourcePreview = false;
            },
            error: () => {
                if (requestId !== this.sourcePreviewRequestId) {
                    return;
                }

                this.selectedSourceFileUrl = '';
                this.sourcePreviewType = 'none';
                this.sourcePreviewError = 'Unable to load source document preview.';
                this.isLoadingSourcePreview = false;
            }
        });
    }

    private async renderPdfCitation(blob: Blob, timelineItem: CitationTimelineItem | undefined, requestId: number): Promise<void> {
        try {
            const workerUrl = new URL('assets/pdf.worker.mjs', document.baseURI).toString();
            pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
            const pdfData = await blob.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
            if (requestId !== this.sourcePreviewRequestId) {
                return;
            }

            const targetPageNumber = Math.min(pdf.numPages, Math.max(1, timelineItem?.page_number ?? 1));
            this.pdfPageNumber = targetPageNumber;
            const pages = [];
            const previewPages: PdfPreviewPage[] = [];
            const containerWidth = this.pdfViewerContainerRef?.nativeElement.clientWidth ?? 0;

            for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
                const page = await pdf.getPage(pageNumber);
                const baseViewport = page.getViewport({ scale: 1 });
                const availableWidth = containerWidth > 24 ? containerWidth - 24 : baseViewport.width;
                const scale = Math.max(0.5, Math.min(2, availableWidth / baseViewport.width));
                const viewport = page.getViewport({ scale });

                pages.push({ page, viewport });
                previewPages.push({
                    pageNumber,
                    width: viewport.width,
                    height: viewport.height,
                    scale,
                    highlight: null
                });
            }

            if (requestId !== this.sourcePreviewRequestId) {
                return;
            }

            this.pdfPages = previewPages;
            this.updatePdfHighlight(timelineItem);
            this.changeDetectorRef?.detectChanges();
            const canvases = await this.waitForPdfCanvases(pdf.numPages, requestId);
            if (!canvases) {
                this.sourcePreviewError = 'Unable to render PDF source preview.';
                this.isLoadingSourcePreview = false;
                return;
            }

            for (let index = 0; index < pages.length; index += 1) {
                if (requestId !== this.sourcePreviewRequestId) {
                    return;
                }

                const { page, viewport } = pages[index];
                const canvas = canvases[index];
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                await page.render({ canvas, canvasContext: canvas.getContext('2d')!, viewport }).promise;
            }

            this.isLoadingSourcePreview = false;
            this.renderedPdfKey = this.buildPdfPreviewKey(this.selectedSourceFileName);
            this.changeDetectorRef?.detectChanges();
            setTimeout(() => this.scrollPdfHighlightIntoView(), 100);
        } catch {
            if (requestId !== this.sourcePreviewRequestId) {
                return;
            }

            try {
                const pdf = await pdfjsLib.getDocument({
                    data: await blob.arrayBuffer(),
                    disableWorker: true
                } as any).promise;

                if (requestId !== this.sourcePreviewRequestId) {
                    return;
                }

                const targetPageNumber = Math.min(pdf.numPages, Math.max(1, timelineItem?.page_number ?? 1));
                this.pdfPageNumber = targetPageNumber;
                const pages = [];
                const previewPages: PdfPreviewPage[] = [];
                const containerWidth = this.pdfViewerContainerRef?.nativeElement.clientWidth ?? 0;

                for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
                    const page = await pdf.getPage(pageNumber);
                    const baseViewport = page.getViewport({ scale: 1 });
                    const availableWidth = containerWidth > 24 ? containerWidth - 24 : baseViewport.width;
                    const scale = Math.max(0.5, Math.min(2, availableWidth / baseViewport.width));
                    const viewport = page.getViewport({ scale });

                    pages.push({ page, viewport });
                    previewPages.push({
                        pageNumber,
                        width: viewport.width,
                        height: viewport.height,
                        scale,
                        highlight: null
                    });
                }

                if (requestId !== this.sourcePreviewRequestId) {
                    return;
                }

                this.pdfPages = previewPages;
                this.updatePdfHighlight(timelineItem);
                this.changeDetectorRef?.detectChanges();
                const canvases = await this.waitForPdfCanvases(pdf.numPages, requestId);
                if (!canvases) {
                    this.sourcePreviewError = 'Unable to render PDF source preview.';
                    this.isLoadingSourcePreview = false;
                    return;
                }

                for (let index = 0; index < pages.length; index += 1) {
                    if (requestId !== this.sourcePreviewRequestId) {
                        return;
                    }

                    const { page, viewport } = pages[index];
                    const canvas = canvases[index];
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    await page.render({ canvas, canvasContext: canvas.getContext('2d')!, viewport }).promise;
                }

                this.isLoadingSourcePreview = false;
                this.renderedPdfKey = this.buildPdfPreviewKey(this.selectedSourceFileName);
                this.changeDetectorRef?.detectChanges();
                setTimeout(() => this.scrollPdfHighlightIntoView(), 100);
            } catch {
                if (requestId !== this.sourcePreviewRequestId) {
                    return;
                }
                this.sourcePreviewError = 'Unable to render PDF source preview.';
                this.isLoadingSourcePreview = false;
            }
        }
    }

    private buildPdfPreviewKey(sourceFileName: string): string {
        return JSON.stringify([this.runId, sourceFileName]);
    }

    private updatePdfHighlight(timelineItem: CitationTimelineItem | undefined): void {
        const targetPageNumber = Math.min(
            this.pdfPages.length,
            Math.max(1, timelineItem?.page_number ?? 1)
        );
        this.pdfPageNumber = targetPageNumber;
        const bbox = timelineItem?.bbox;

        this.pdfPages.forEach((page) => {
            page.highlight = page.pageNumber === targetPageNumber && bbox && bbox.length === 4
                ? this.buildPdfHighlight(bbox, page)
                : null;
        });
    }

    private buildPdfHighlight(
        bbox: [number, number, number, number],
        page: PdfPreviewPage
    ): NonNullable<PdfPreviewPage['highlight']> {
        const horizontalPadding = 8;
        const verticalPadding = 6;
        const left = Math.max(0, Math.min(bbox[0], bbox[2]) * page.scale - horizontalPadding);
        const top = Math.max(0, Math.min(bbox[1], bbox[3]) * page.scale - verticalPadding);
        const right = Math.min(page.width, Math.max(bbox[0], bbox[2]) * page.scale + horizontalPadding);
        const bottom = Math.min(page.height, Math.max(bbox[1], bbox[3]) * page.scale + verticalPadding);
        return {
            left,
            top,
            width: right - left,
            height: bottom - top
        };
    }

    private waitForPdfCanvases(expectedCount: number, requestId: number): Promise<HTMLCanvasElement[] | null> {
        return new Promise((resolve) => {
            const findCanvases = (): void => {
                if (requestId !== this.sourcePreviewRequestId) {
                    resolve(null);
                    return;
                }

                const canvases = this.pdfPageCanvasRefs?.map((canvasRef) => canvasRef.nativeElement) ?? [];
                if (canvases.length === expectedCount) {
                    resolve(canvases);
                    return;
                }

                window.requestAnimationFrame(findCanvases);
            };

            findCanvases();
        });
    }

    private scrollPdfHighlightIntoView(): void {
        const highlight = this.pdfViewerContainerRef?.nativeElement.querySelector('.pdf-citation-highlight') as HTMLElement | null;
        highlight?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    private loadDeltaSourcePreview(
        params: CitationSourceMetadataParams,
        requestId: number,
        timelineItem?: CitationTimelineItem
    ): void {
        if (!params.rowUid || !params.rowUidColumn) {
            this.sourcePreviewError = 'This delta citation is missing the row identity needed to load the record.';
            this.isLoadingSourcePreview = false;
            return;
        }

        this.citationsApiService!.getDeltaSourceRows<CitationDeltaSourceResponse>(
            params,
            this.runId,
            this.refineryId,
            this.unitId
        ).subscribe({
            next: (response) => {
                if (requestId !== this.sourcePreviewRequestId) {
                    return;
                }

                const rows = Array.isArray(response?.rows) ? response.rows : [];
                const columns = Array.isArray(response?.columns) ? response.columns : [];

                this.sourceDeltaRowCount = response?.row_count ?? rows.length;
                this.sourceDeltaRecords = rows.map((row) => this.buildDeltaFieldRows(row, columns, timelineItem));
                this.isLoadingSourcePreview = false;
                setTimeout(() => this.scrollDeltaHighlightIntoView(), 100);
            },
            error: () => {
                if (requestId !== this.sourcePreviewRequestId) {
                    return;
                }

                this.sourceDeltaRecords = [];
                this.sourceDeltaRowCount = 0;
                this.sourcePreviewError = 'Unable to load the delta source record.';
                this.isLoadingSourcePreview = false;
            }
        });
    }

    private buildDeltaFieldRows(
        row: Record<string, unknown>,
        columns: string[],
        timelineItem?: CitationTimelineItem
    ): DeltaFieldRow[] {
        const orderedKeys = columns.length > 0 ? columns : Object.keys(row ?? {});

        return orderedKeys.map((key) => {
            const value = this.formatDeltaValue(row?.[key]);
            return {
                key,
                value,
                parts: this.buildSourceTextParts(value, timelineItem?.text_snippet)
            };
        });
    }

    private buildSourceTextParts(value: string, textSnippet = ''): SourceTextPart[] {
        const snippet = textSnippet.trim();
        if (!snippet) {
            return [{ text: value, highlighted: false }];
        }

        const start = value.toLocaleLowerCase().indexOf(snippet.toLocaleLowerCase());
        if (start < 0) {
            return [{ text: value, highlighted: false }];
        }

        return [
            { text: value.slice(0, start), highlighted: false },
            { text: value.slice(start, start + snippet.length), highlighted: true },
            { text: value.slice(start + snippet.length), highlighted: false }
        ].filter((part) => part.text.length > 0);
    }

    private scrollDeltaHighlightIntoView(): void {
        const container = this.deltaRecordsContainerRef?.nativeElement;
        const highlight = container?.querySelector('.source-record-highlight') as HTMLElement | null;
        highlight?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    private formatDeltaValue(value: unknown): string {
        if (value === null || value === undefined || value === '') {
            return '--';
        }

        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        }

        return String(value);
    }

    private resetSourcePreview(): void {
        this.sourcePreviewRequestId += 1;
        this.revokeSourceObjectUrl();
        this.selectedSourceFileUrl = '';
        this.selectedSourceFileName = '';
        this.sourcePreviewType = 'none';
        this.sourcePreviewError = '';
        this.isLoadingSourcePreview = false;
        this.sourceCsvRows = [];
        this.sourceExcelRows = [];
        this.sourceCsvVisibleRows = [];
        this.sourceExcelVisibleRows = [];
        this.sourceCsvHighlightedRowNumber = null;
        this.sourceExcelHighlightedRowNumber = null;
        this.sourceDeltaRecords = [];
        this.sourceDeltaRowCount = 0;
        this.pdfPages = [];
        this.pdfPageNumber = 0;
        this.renderedPdfKey = '';
    }

    private scrollTabularTargetRowIntoView(type: 'csv' | 'excel'): void {
        const container = type === 'csv' ? this.csvTableContainerRef?.nativeElement : this.excelTableContainerRef?.nativeElement;
        if (!container) {
            return;
        }

        const targetRow = container.querySelector('tr.target-row') as HTMLTableRowElement | null;
        if (!targetRow) {
            return;
        }

        // Calculate the position to center the target row in the viewport
        const containerRect = container.getBoundingClientRect();
        const rowRect = targetRow.getBoundingClientRect();
        
        // Position the row in the center of the visible area
        const targetScrollTop = targetRow.offsetTop - (container.clientHeight / 2) + (targetRow.clientHeight / 2);
        
        // Ensure we don't scroll beyond the boundaries
        const maxScroll = container.scrollHeight - container.clientHeight;
        const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScroll));
        
        container.scrollTo({
            top: finalScrollTop,
            behavior: 'smooth'
        });
    }

    private buildTabularPreviewRows(
        allRows: string[][],
        timelineItem?: CitationTimelineItem
    ): { rows: TabularPreviewRow[]; targetRowNumber: number | null } {
        if (allRows.length <= 1) {
            return { rows: [], targetRowNumber: null };
        }

        const targetRowNumber = this.resolveTabularTargetRowNumber(allRows, timelineItem);
        const dataRows = allRows.slice(1);
        const maxVisibleRows = 120;

        let startIndex = 0;
        if (targetRowNumber && targetRowNumber > 0) {
            const targetZeroBased = targetRowNumber - 1;
            startIndex = Math.max(0, targetZeroBased - 20);
        }

        if (startIndex + maxVisibleRows > dataRows.length) {
            startIndex = Math.max(0, dataRows.length - maxVisibleRows);
        }

        const endIndex = Math.min(dataRows.length, startIndex + maxVisibleRows);
        const visibleRows = dataRows.slice(startIndex, endIndex).map((cells, index) => {
            const dataRowNumber = startIndex + index + 1;
            return {
                cells,
                dataRowNumber,
                isTarget: targetRowNumber === dataRowNumber
            };
        });

        return {
            rows: visibleRows,
            targetRowNumber
        };
    }

    private resolveTabularTargetRowNumber(
        allRows: string[][],
        timelineItem?: CitationTimelineItem
    ): number | null {
        if (!timelineItem || allRows.length <= 1) {
            return null;
        }

        const headerRow = allRows[0] ?? [];
        const dataRows = allRows.slice(1);
        const targetUid = (timelineItem.row_uid ?? '').toString().trim();
        const targetUidColumn = (timelineItem.row_uid_column ?? '').toString().trim().toLowerCase();

        if (targetUid) {
            let columnIndex = -1;
            if (targetUidColumn) {
                columnIndex = headerRow.findIndex((headerCell) => headerCell.trim().toLowerCase() === targetUidColumn);
            }

            if (columnIndex >= 0) {
                const rowIndex = dataRows.findIndex((row) => (row[columnIndex] ?? '').toString().trim() === targetUid);
                if (rowIndex >= 0) {
                    return rowIndex + 1;
                }
            }

            const fallbackRowIndex = dataRows.findIndex((row) => row.some((cell) => (cell ?? '').toString().trim() === targetUid));
            if (fallbackRowIndex >= 0) {
                return fallbackRowIndex + 1;
            }
        }

        const rowNumberValue = timelineItem.row_number;
        if (rowNumberValue !== null && rowNumberValue !== undefined && Number.isFinite(Number(rowNumberValue))) {
            const numericRowNumber = Math.trunc(Number(rowNumberValue));
            const candidateFromSheetNumber = numericRowNumber - 1;
            if (candidateFromSheetNumber >= 1 && candidateFromSheetNumber <= dataRows.length) {
                return candidateFromSheetNumber;
            }

            if (numericRowNumber >= 1 && numericRowNumber <= dataRows.length) {
                return numericRowNumber;
            }
        }

        const rowIndexValue = timelineItem.row_index;
        if (rowIndexValue !== null && rowIndexValue !== undefined && Number.isFinite(Number(rowIndexValue))) {
            const numericRowIndex = Math.trunc(Number(rowIndexValue));
            const zeroBasedCandidate = numericRowIndex + 1;
            if (zeroBasedCandidate >= 1 && zeroBasedCandidate <= dataRows.length) {
                return zeroBasedCandidate;
            }

            if (numericRowIndex >= 1 && numericRowIndex <= dataRows.length) {
                return numericRowIndex;
            }
        }

        return null;
    }

    private resolveSourcePreviewType(extension: string, timelineItem?: CitationTimelineItem): SourcePreviewType {
        // The citation's own source_type is authoritative; delta refs have no file extension.
        const declaredType = (timelineItem?.source_type ?? '').trim().toLowerCase();
        if (declaredType === 'delta') {
            return 'delta';
        }

        if (extension === 'pdf') {
            return 'pdf';
        }

        if (extension === 'csv') {
            return 'csv';
        }

        if (extension === 'xlsx' || extension === 'xls') {
            return 'excel';
        }

        return 'unsupported';
    }

    private parseCsvRows(csvText: string): string[][] {
        const rows: string[][] = [];
        const lines = csvText.split(/\r?\n/).filter((line) => line.length > 0);

        lines.forEach((line) => {
            rows.push(this.parseCsvLine(line));
        });

        return rows;
    }

    private parseCsvLine(line: string): string[] {
        const cells: string[] = [];
        let currentCell = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i += 1) {
            const char = line[i];

            if (char === '"') {
                const nextChar = line[i + 1];
                if (inQuotes && nextChar === '"') {
                    currentCell += '"';
                    i += 1;
                } else {
                    inQuotes = !inQuotes;
                }
                continue;
            }

            if (char === ',' && !inQuotes) {
                cells.push(currentCell.trim());
                currentCell = '';
                continue;
            }

            currentCell += char;
        }

        cells.push(currentCell.trim());
        return cells;
    }

    private revokeSourceObjectUrl(): void {
        if (this.activeSourceObjectUrl) {
            URL.revokeObjectURL(this.activeSourceObjectUrl);
            this.activeSourceObjectUrl = '';
        }
    }

    private onResizeMove(event: MouseEvent): void {
        if (!this.activeResizeSide || !this.citationsGridRef?.nativeElement) {
            return;
        }

        const gridRect = this.citationsGridRef.nativeElement.getBoundingClientRect();
        const totalWidth = gridRect.width;

        if (totalWidth <= 0) {
            return;
        }

        const xWithinGrid = Math.max(0, Math.min(totalWidth, event.clientX - gridRect.left));

        if (this.activeResizeSide === 'left') {
            const proposedLeft = (xWithinGrid / totalWidth) * 100;
            const maxLeft = 100 - this.minCenterPanelPercent - this.rightPanelWidthPercent;
            const nextLeft = this.clamp(proposedLeft, this.minLeftPanelPercent, maxLeft);

            this.leftPanelWidthPercent = nextLeft;
            this.centerPanelWidthPercent = 100 - this.leftPanelWidthPercent - this.rightPanelWidthPercent;
            return;
        }

        const proposedLeftAndCenter = (xWithinGrid / totalWidth) * 100;
        const minBoundary = this.leftPanelWidthPercent + this.minCenterPanelPercent;
        const maxBoundary = 100 - this.minRightPanelPercent;
        const constrainedBoundary = this.clamp(proposedLeftAndCenter, minBoundary, maxBoundary);

        this.centerPanelWidthPercent = constrainedBoundary - this.leftPanelWidthPercent;
        this.rightPanelWidthPercent = 100 - this.leftPanelWidthPercent - this.centerPanelWidthPercent;
    }

    private onResizeEnd(): void {
        this.activeResizeSide = null;
        this.detachResizeListeners();
    }

    private detachResizeListeners(): void {
        if (this.windowMouseMoveHandler) {
            window.removeEventListener('mousemove', this.windowMouseMoveHandler);
        }

        if (this.windowMouseUpHandler) {
            window.removeEventListener('mouseup', this.windowMouseUpHandler);
        }

        this.windowMouseMoveHandler = undefined;
        this.windowMouseUpHandler = undefined;
    }

    private clamp(value: number, min: number, max: number): number {
        return Math.max(min, Math.min(max, value));
    }
}
